import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { Button, toastCaller } from '@/shared/ui';

import {
  getSchemasUsingSeries,
  templatesModel,
  type DocumentTemplate,
  type TemplateIssue,
} from '@/entities/templates';

import type { TemplateVersionTarget } from '../config';
import { useApplyTemplateVersion, useTemplateIssueText } from '../hooks';
import {
  findStructureIssues,
  findTemplateIssuePlace,
  getSaveRefusalIssues,
  getSaveRefusalText,
  locateTemplateIssues,
  splitSaveRefusal,
  toSavedTemplateSections,
} from '../lib';

import styles from './TemplateSaveBar.module.scss';

type Props = TemplateVersionTarget & {
  // What a block template's document cannot save as it is, marked there.
  blockingIssues?: TemplateIssue[];
  className?: string;
  onSaved: () => void;
  onDiscard: () => void;
  // "Keep editing": the changes go back to the edit mode.
  onResume?: () => void;
  // A refused block template names its problems; they are marked where
  // they are.
  onRefused?: (issues: TemplateIssue[]) => void;
  // Leads to the line, else the section, a refused problem is in.
  onShowIssue?: (targetId: string) => void;
  // The series' current version, read again after another save replaced the
  // source: the edit starts again on it.
  onReloaded?: (current: DocumentTemplate) => void;
};

// moved: another save replaced the source, so the edit has to start again on
// the current version; busy: another save is still replacing it.
// Otherwise what scribe named: the issues of a refused block template, else
// its reply split into problems.
type Failure =
  | { kind: 'moved'; reloadFailed?: boolean }
  | { kind: 'busy' }
  | { kind: 'refused'; issues: TemplateIssue[]; problems: string[] };

// The template changes left in the edit mode: saved as the clinic's next
// version, which the encounters started later take, or discarded.
export const TemplateSaveBar: FC<Props> = ({
  draft,
  source,
  blockingIssues,
  className,
  onSaved,
  onDiscard,
  onResume,
  onRefused,
  onShowIssue,
  onReloaded,
}) => {
  const { formatMessage, formatList } = useIntl();
  const issueText = useTemplateIssueText();
  const { save: saveVersion, reload } = useApplyTemplateVersion();
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const schemas = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  );
  const sharedBy = getSchemasUsingSeries(schemas, source.series_id);
  const blocked =
    findStructureIssues(draft.template).length > 0 || !!blockingIssues?.length;
  // Saving again would be refused the same way until the source is reloaded.
  const moved = failure?.kind === 'moved';
  const pending = saving || reloading;

  const save = async () => {
    setSaving(true);
    setFailure(null);
    try {
      await saveVersion({ draft, source });
      setSaving(false);
      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'workspace.template.versionSaved',
          defaultMessage:
            'Saved as a new version of the clinic template. Encounters started from now on use it.',
        }),
      });
      onSaved();
    } catch (error) {
      const issues = locateTemplateIssues({
        issues: getSaveRefusalIssues(error),
        sent: toSavedTemplateSections(draft.template),
      });
      const refusal = getSaveRefusalText(error);
      setSaving(false);
      if (templatesModel.isSeriesBusyWriteError(error)) {
        setFailure({ kind: 'busy' });
      } else if (templatesModel.isSeriesMovedWriteError(error)) {
        setFailure({ kind: 'moved' });
      } else {
        setFailure({
          kind: 'refused',
          issues,
          problems: refusal && !issues.length ? splitSaveRefusal(refusal) : [],
        });
      }
      onRefused?.(issues);
    }
  };

  // A source still current means the other save has not landed yet.
  const reloadSource = async () => {
    setReloading(true);
    const current = await reload(source).catch(() => undefined);
    setReloading(false);
    if (!current) {
      setFailure({ kind: 'moved', reloadFailed: true });
      return;
    }
    if (current.id === source.id) {
      setFailure({ kind: 'busy' });
      return;
    }
    setFailure(null);
    onReloaded?.(current);
  };

  return (
    <div
      className={cn(styles.bar, className)}
      role="region"
      aria-label={formatMessage({
        id: 'workspace.templateEdit.saveBar',
        defaultMessage: 'Template changes',
      })}
      data-component="workspace-template-save-bar"
    >
      <p className={cn('p3', styles.message)}>
        <FormattedMessage
          id="workspace.templateEdit.unsaved"
          defaultMessage="“{name}” has changes that are not saved."
          values={{ name: source.name }}
        />
      </p>
      <p
        className={cn('p4', styles.hint)}
        data-component="workspace-template-save-applies"
      >
        <FormattedMessage
          id="workspace.templateEdit.appliesLater"
          defaultMessage="Encounters started from now on use the saved version."
        />
      </p>
      {sharedBy.length > 1 && (
        <p
          className={cn('p4', styles.hint)}
          data-component="workspace-field-list-shared"
        >
          <FormattedMessage
            id="workspace.fieldList.sharedBySchemas"
            defaultMessage="This document is used by {count, plural, one {# schema} other {# schemas}} ({schemas}); the new version applies to all of them."
            values={{
              count: sharedBy.length,
              schemas: formatList(
                sharedBy.map((schema) => schema.name),
                { type: 'unit' },
              ),
            }}
          />
        </p>
      )}
      {blocked && (
        <p className={cn('p3', styles.warning)}>
          <FormattedMessage
            id="workspace.template.saveBlocked"
            defaultMessage="Fix the marked sections and fields to save"
          />
        </p>
      )}
      <div className={styles.actions}>
        <Button
          size="small"
          variant="primary"
          loading={saving}
          disabled={pending || moved || blocked}
          data-component="workspace-template-save-version"
          onClick={() => void save()}
        >
          <FormattedMessage
            id="workspace.templateEdit.save"
            defaultMessage="Save as clinic version"
          />
        </Button>
        <Button
          variant="tertiary"
          size="small"
          disabled={pending}
          data-component="workspace-template-discard"
          onClick={onDiscard}
        >
          <FormattedMessage
            id="workspace.templateEdit.discard"
            defaultMessage="Discard changes"
          />
        </Button>
        {onResume && (
          <Button
            variant="tertiary"
            size="small"
            disabled={pending}
            data-component="workspace-template-keep-editing"
            onClick={onResume}
          >
            <FormattedMessage
              id="workspace.stay"
              defaultMessage="Keep editing"
            />
          </Button>
        )}
      </div>
      {failure?.kind === 'moved' && (
        <div
          role="alert"
          className={cn('p3', styles.alert)}
          data-component="workspace-template-series-moved"
        >
          <p>
            <FormattedMessage
              id="workspace.templateEdit.seriesMoved"
              defaultMessage="The template was changed elsewhere while you were editing it. Load its current version; your changes are then listed to make again by hand."
            />
          </p>
          {failure.reloadFailed && (
            <p>
              <FormattedMessage
                id="workspace.templateEdit.reloadFailed"
                defaultMessage="The current version of the template could not be loaded. Try again."
              />
            </p>
          )}
          {onReloaded && (
            <Button
              variant="secondary"
              size="small"
              className={styles.reload}
              loading={reloading}
              disabled={reloading}
              data-component="workspace-template-reload"
              onClick={() => void reloadSource()}
            >
              <FormattedMessage
                id="workspace.templateEdit.reload"
                defaultMessage="Load the current version"
              />
            </Button>
          )}
        </div>
      )}
      {failure?.kind === 'busy' && (
        <p
          role="alert"
          className={cn('p3', styles.alert)}
          data-component="workspace-template-save-busy"
        >
          <FormattedMessage
            id="workspace.templateEdit.saveBusy"
            defaultMessage="Another save of this template is still under way. Try again in a moment."
          />
        </p>
      )}
      {failure?.kind === 'refused' && (
        <div role="alert" className={cn('p3', styles.alert)}>
          <p>
            <FormattedMessage
              id="workspace.template.versionFailed"
              defaultMessage="The new template version was not saved. Your changes are kept; try again."
            />
          </p>
          {failure.issues.length > 0 && (
            <ul
              className={styles.refusal}
              data-component="workspace-template-refused-issues"
            >
              {failure.issues.map((issue, index) => {
                const place = findTemplateIssuePlace(draft.template, issue);
                const problem = issueText.describe(issue);
                return (
                  <li key={`${issue.Kind}/${index}`}>
                    {place ? (
                      <button
                        type="button"
                        className={styles.issueLink}
                        disabled={!onShowIssue}
                        title={issue.Message || undefined}
                        onClick={() => onShowIssue?.(place.targetId)}
                      >
                        {issueText.describePlace(place)}: {problem}
                      </button>
                    ) : (
                      <span title={issue.Message || undefined}>{problem}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {failure.problems.length > 0 && (
            <>
              <p>
                <FormattedMessage
                  id="workspace.template.versionRefusal"
                  defaultMessage="Server reply:"
                />
              </p>
              <ul className={styles.refusal}>
                {failure.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};
